import { useLazyAsyncData } from '#imports'
import { type MaybeRefOrGetter, toValue } from 'vue'

import {
  type PaymentMethod,
  PaymentMethodCode,
} from '@backmarket/http-api/src/api-specs-payment/payment/payment.types'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'

import { PAYMENT_METHOD_CONFIGS } from '../config/methods'
import type { PaymentMethodAvailabilityOptions } from '../config/methods.type'

const PAYMENT_METHOD_AVAILABILITY_TIMEOUT = 800
const PAYMENT_METHODS_WITH_AVAILABILITY_TIMEOUT: PaymentMethodCode[] = [
  PaymentMethodCode.GOOGLE_PAY,
] as const

export function usePaymentMethodAvailabilityContext(
  paymentMethod: PaymentMethod,
  options?: PaymentMethodAvailabilityOptions,
) {
  const config = PAYMENT_METHOD_CONFIGS[paymentMethod.bmCode]

  return !!config && 'useAvailabilityContext' in config
    ? config.useAvailabilityContext(paymentMethod || {}, options)
    : undefined
}

async function isPaymentMethodAvailable(
  paymentMethod: PaymentMethod,
  context: unknown,
) {
  const isAvailableInBrowser =
    PAYMENT_METHOD_CONFIGS[paymentMethod.bmCode]?.isAvailableInBrowser ||
    (() => Promise.resolve(true))

  if (process.server) {
    // It should never happen that we check the availability of a payment method on the server side.
    // But if it does, we consider the payment method as available.
    return true
  }

  const availabilityCheck = isAvailableInBrowser(
    paymentMethod.config || {},
    context,
  ).catch((error: unknown) => {
    useLogger().error('[PAYMENT] Payment method availability check failed', {
      error,
      payment: {
        method: paymentMethod,
      },
      owners: ['bot-squad-payin-front'],
    })

    // Consider the payment method unavailable on failure.
    return false
  })

  let didTimeout = false
  let timeoutId: ReturnType<typeof setTimeout> | undefined
  const timeout = PAYMENT_METHODS_WITH_AVAILABILITY_TIMEOUT.includes(
    paymentMethod.bmCode,
  )
    ? new Promise<false>((resolve) => {
        timeoutId = setTimeout(() => {
          didTimeout = true
          resolve(false)
        }, PAYMENT_METHOD_AVAILABILITY_TIMEOUT)
      })
    : undefined

  // When a timeout occurs we want a log to inform us
  // that the user could have paid if we waited longer.
  void availabilityCheck.then((isAvailable) => {
    if (didTimeout && isAvailable) {
      useLogger().info('[PAYMENT] user_could_have_pay', {
        payment: {
          event: { key: 'user_could_have_pay' },
          method: paymentMethod,
          paymentMethodCode: paymentMethod.bmCode,
        },
        owners: ['bot-squad-payin-front'],
      })
    }
  })

  try {
    return timeout
      ? await Promise.race([availabilityCheck, timeout])
      : await availabilityCheck
  } finally {
    if (timeoutId !== undefined) {
      clearTimeout(timeoutId)
    }
  }
}

/**
 * Filter a list of payment methods, and retain only those that are available
 * in the user's browser. It relies on `isAvailableInBrowser` from
 * `PAYMENT_METHOD_CONFIGS`, or consider it is always available if not defined.
 * Note that it will return the same list on server-side.
 */
export async function filterAvailablePaymentMethods(
  paymentMethods: PaymentMethod[],
  contexts: Record<PaymentMethodCode, unknown>,
) {
  const availability = await Promise.all(
    paymentMethods.map((paymentMethod) =>
      isPaymentMethodAvailable(paymentMethod, contexts[paymentMethod.bmCode]),
    ),
  )

  return paymentMethods.filter((_, i) => availability[i])
}

/**
 * Get the payment methods available in the current browser context.
 * This will only resolve in the browser and keep pending on the server.
 */
export const useAvailablePaymentMethods = (
  paymentMethods: MaybeRefOrGetter<PaymentMethod[]>,
  options?: PaymentMethodAvailabilityOptions,
) => {
  const contexts = toValue(paymentMethods).reduce(
    (ctx, paymentMethod) => ({
      ...ctx,
      [paymentMethod.bmCode]: usePaymentMethodAvailabilityContext(
        paymentMethod,
        options,
      ),
    }),
    {} as Record<PaymentMethodCode, unknown>,
  )

  const asyncData = useLazyAsyncData(
    () => filterAvailablePaymentMethods(toValue(paymentMethods), contexts),
    {
      server: false,
      default: () => toValue(paymentMethods),
    },
  )

  return { ...asyncData, availablePaymentMethods: asyncData.data }
}
